import { serialize } from "object-to-formdata"
import { message } from "antd"
import { createPaymentIntent, capturePayment, resetExperienceBookingData } from "store/experiences"
import dayjs from "dayjs"
import { saveGuideBooking, setClientId, createGuideOrder, fetchGuideOrder, createGuideOrderPayment, captureGuideOrderPayment } from "store/bookings"
import { setSelectedPaymentMethodId } from "store/user"
import { initialValues, PAYMENT_OPTIONS, BOOKING_SOURCE } from "./constants"
import { buildAddOnSelectionsAttributes } from "components/bookings/helpers"
import { buildBreakdownFromPaymentSummary, computeGuestSubtotalFromPaymentSummary, withSoldUnitPrices } from "utils/calculateBookingTotal"
import { paymentProvider } from "ui/Booking/Details/Body/PaymentSummary/Modals/TakePayment/TakePaymentForm/constants"
import { calculatePaymentBreakdown, getSavedBookingTotals, sumGuestTypeCount } from "utils/bookingCalculations"
import { countDays, isDynamicMultiDay } from "utils/multiDayPricing"
import { getOrderError } from "pagesV2/BookingWidget/WidgetCheckout/payment"
import { toaster } from "components/common/Toast"
import i18n from "i18n"

export const handleNameKeyDown = (e) => {
  const key = e.key
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"]

  if (/^[A-Za-z]$/.test(key) || allowedKeys.includes(key)) {
    // allowed, do nothing
    return
  }
  e.preventDefault()
}

// Utility function to handle trip booking submission

const buildClientAttributes = (values, client) => {
  // If existing client, only send phone if missing on selection
  if (values?.isExistingClient) {
    if (values?.existing_client?.phone_number) {
      return {}
    }
    return { phone_number: values?.phone_number?.fullNumber, country_code: values?.phone_number?.countryCode }
  }
  // If client object exists, only send phone if missing on user
  if (client?.user_id) {
    if (client?.user?.phone_number) {
      return {}
    }
    return { phone_number: values?.phone_number?.fullNumber, country_code: values?.phone_number?.countryCode }
  }
  // For new clients, send all details
  return {
    first_name: values?.firstName,
    last_name: values?.lastName,
    email: values?.email ?? "",
    phone_number: values?.phone_number?.fullNumber,
    country_code: values?.phone_number?.countryCode,
    full_name: values?.existing_client?.label,
    ...(values?.email ? {} : { no_email: true })
  }
}

const getPaymentType = (values, booking) => {
  const { deposit_later, deposit_cash, full_now, full_cash, full_link, deposit_link, deposit_link_in_cash, pay_later } = PAYMENT_OPTIONS
  // Define pay later eligible payment options
  const PAY_LATER_OPTIONS = [full_now, pay_later]
  const PAY_LINK_OPTIONS = [full_link]

  // Handle pay later case
  if (PAY_LATER_OPTIONS.includes(booking?.payment_option) && values.collectPayment === "no") {
    return pay_later
  }
  if (PAY_LINK_OPTIONS.includes(booking?.payment_option) && values.collectPayment === "no") {
    return full_link
  }

  if (values?.paymentType === "paypalLink" || values?.paymentType === "powertranzLink") {
    if (values?.depositOnly) {
      return values?.paymentOption === deposit_cash ? deposit_link_in_cash : deposit_link
    }
    return full_link
  }

  // PowerTranz card from the guide's form: charged on the hosted page right after the save. The
  // balance can't be charged to the card later (no saved cards yet), so a deposit's balance is cash.
  if (values?.paymentType === "powertranzCard") {
    return values?.depositOnly ? deposit_cash : full_now
  }

  // Handle card payments
  if (values?.paymentType === "card") {
    // Prefer the live form selection (radio) over the saved booking value, otherwise editing a
    // booking always re-sends its original payment_option and toggling the deposit radio is ignored.
    switch (values?.paymentOption || booking?.payment_option) {
      case deposit_link_in_cash:
      case deposit_cash:
        return deposit_cash
      case deposit_link:
      case deposit_later:
        return deposit_later
      default:
        return full_now
    }
  }

  // Handle cash payment
  if (values?.paymentType === "cash") {
    return full_cash
  }

  // Handle link payment with deposit
  if (values?.paymentType === "link") {
    if (values?.depositOnly) {
      return values?.paymentOption === deposit_cash ? deposit_link_in_cash : deposit_link
    }
    return full_link
  }

  // Default to the existing payment option if none of the above conditions match
  return values?.paymentOption
}

// The booking's guest counts as the API takes them. Flat rate (waterfall): the included guests go in
// *_count, adults first, and the rest in extra_*_count; passengers are always extra. Any other
// pricing: every guest in *_count.
const splitGuestCounts = (bookingSelections) => {
  const priceData = bookingSelections?.tripLength?.price || {}
  const { pricing_type, included_guests, max_guests } = priceData

  const mainCounts = bookingSelections?.counts?.main || {}
  const extraCounts = bookingSelections?.counts?.extra || {}

  const totalCounts = {
    adults: (Number(mainCounts.adults) || 0) + (Number(extraCounts.adults) || 0),
    children: (Number(mainCounts.children) || 0) + (Number(extraCounts.children) || 0),
    infants: (Number(mainCounts.infants) || 0) + (Number(extraCounts.infants) || 0),
    passengers: (Number(mainCounts.passengers) || 0) + (Number(extraCounts.passengers) || 0)
  }

  let finalIncluded = { adults: 0, children: 0, infants: 0, passengers: 0 }
  let finalExtra = { adults: 0, children: 0, infants: 0, passengers: 0 }

  if (pricing_type === "charge_flat_rate") {
    const limit = included_guests ?? max_guests ?? 0
    let remaining = limit

    // Adults First
    const adultsIn = Math.min(totalCounts.adults, remaining)
    remaining = Math.max(0, remaining - adultsIn)
    finalExtra.adults = Math.max(0, totalCounts.adults - adultsIn)
    finalIncluded.adults = adultsIn

    // Children
    const childrenIn = Math.min(totalCounts.children, remaining)
    remaining = Math.max(0, remaining - childrenIn)
    finalExtra.children = Math.max(0, totalCounts.children - childrenIn)
    finalIncluded.children = childrenIn

    // Infants
    const infantsIn = Math.min(totalCounts.infants, remaining)
    finalExtra.infants = Math.max(0, totalCounts.infants - infantsIn)
    finalIncluded.infants = infantsIn

    // Passengers (Always Extra in Flat Rate)
    finalExtra.passengers = totalCounts.passengers
    finalIncluded.passengers = 0
  } else {
    // Per Person or other types: All in Main
    finalIncluded = { ...totalCounts }
    finalExtra = { adults: 0, children: 0, infants: 0, passengers: 0 }
  }

  return {
    adult_count: finalIncluded.adults,
    child_count: finalIncluded.children,
    infant_count: finalIncluded.infants,
    passenger_count: finalIncluded.passengers,
    extra_adult_count: finalExtra.adults,
    extra_child_count: finalExtra.children,
    extra_infant_count: finalExtra.infants,
    extra_passenger_count: finalExtra.passengers
  }
}

const buildTripPayload = ({
  values,
  bookingSelections,
  timeRange: { start_time, end_time },
  paymentType,
  clientInfo: { includeClientAttributes, clientId, client, is_refund },
  selectedPaymentMethodId,
  isEdit,
  isExperienceChanged,
  depositType,
  paymentProvider,
  appliedPromos = [],
  existingBookingAddOns = [],
  isPostTrip = false
}) => {
  let depositTypeToSend = "default"
  if (isEdit) {
    depositTypeToSend = depositType
  }
  if (values?.customDepositAmount) {
    depositTypeToSend = "custom"
  }
  // A ticketed booking books a start time and tickets (the start time gives the date), on create and
  // on edit (QA Ticketing #57); every other booking its date, duration, start time and guests.
  const scheduleFields =
    bookingSelections?.scheduledSlot
      ? { experience_time_slot_id: bookingSelections.scheduledSlot.id, ticket_lines: buildTicketLines(bookingSelections) }
      : {
          booked_date: bookingSelections?.date,
          // Dynamic multi-day is the one shape whose length the backend can't derive: fixed durations
          // come from trip_price_id. `endDate` is only ever set by the dynamic range picker, so its
          // presence is what marks the booking as guide-chosen-span.
          ...(bookingSelections?.endDate ? { duration_days: countDays(bookingSelections.date, bookingSelections.endDate) } : {}),
          ...splitGuestCounts(bookingSelections),
          start_time,
          end_time,
          trip_price_id: bookingSelections?.tripLength?.key
        }

  // DS-4473. Sending a card id makes the backend charge it at save time. On an adjustment to a
  // trip that already happened that must be a deliberate act: the operator picks "collect now" or
  // the increase lands as an outstanding balance to collect later. Every other flow keeps sending
  // the card as before.
  const isCollectingNow = values?.collectPayment === "yes"
  // Sending a link means the client pays it themselves, so no card may go with it — a card id the
  // guide selected before switching to "Send payment link" would otherwise be charged on save.
  const isPayingByLink = ["link", "paypalLink", "powertranzLink"].includes(values?.paymentType)
  // A PowerTranz card never comes from the Stripe card list either.
  const isPowerTranzCard = values?.paymentType === "powertranzCard"
  const paymentMethodToSend = isPayingByLink || isPowerTranzCard || (isPostTrip && !isCollectingNow) ? null : selectedPaymentMethodId

  const payload = {
    trip_id: values?.experience?.key,
    accessibility_enabled: values?.accessibility_required,
    accessibility_notes: values?.accessibility_notes,
    note: values?.notes,
    ...scheduleFields,
    deposit_percentage: values?.customDepositAmount || values?.depositPercent,
    deposit_type: depositTypeToSend,
    payment_option: paymentType,
    record_now: getRecordNowValue(values?.collectPayment),
    record_amount: values?.recordPayment,
    payment_method_id: paymentMethodToSend,
    refund_by: values?.refundBy,
    is_refund,
    payment_provider: paymentProvider,
    booking_source: BOOKING_SOURCE.manual,
    promo_codes: appliedPromos.map((p) => p.code),
    send_guest_email: values?.send_guest_email ?? true,
    booking_add_ons_attributes: buildAddOnSelectionsAttributes(bookingSelections?.addOns, existingBookingAddOns)
  }

  // Tell the backend whether the experience/trip was swapped while editing an existing booking, so
  // it can re-point the booking to the new trip (and recalc against it) rather than editing the
  // original in place. Sent on edit only — a new booking has no existing trip to change — and never
  // for a ticketed booking, which can't move to another experience.
  if (isEdit && !bookingSelections?.scheduledSlot) {
    payload["allow_trip_change"] = isExperienceChanged
  }

  if (clientId) {
    payload["client_id"] = clientId
  }

  if (includeClientAttributes) {
    const clientAttrs = buildClientAttributes(values, client)
    if (clientAttrs && Object.keys(clientAttrs).length > 0) {
      payload["client_attributes"] = clientAttrs
    }
  }

  // Subtotal adjustment (create-screen only): the guide can negotiate a custom base subtotal.
  // `adjusted_subtotal` is the new base (excludes add-ons); the backend recalculates all fees
  // and the total on it. Send it only when actually adjusted; post-creation editing is a
  // different flow and is not supported here.
  const adjustedSubtotal = Number(values?.adjustedPrice)
  if (!isEdit && values?.adjustedPrice != null && values?.adjustedPrice !== "" && !isNaN(adjustedSubtotal) && adjustedSubtotal > 0) {
    payload["adjusted_subtotal"] = adjustedSubtotal
    if (values?.adjustmentReason) {
      payload["adjustment_reason"] = values.adjustmentReason
    }
  }

  return payload
}

const getRecordNowValue = (collectPaymentValue) => {
  if (collectPaymentValue === "yes") return true
  if (collectPaymentValue === "no") return false
  return null
}

export const submitTripBooking = async ({
  form,
  dispatch,
  stripe,
  bookingSelections,
  // The selected experience is multi-day: its start time comes from its schedule and the backend
  // overwrites any it is sent, so none is sent (an edit would otherwise resend the saved one).
  isMultiDay = false,
  selectedPaymentMethodId,
  bookingUuid,
  clientId,
  isBookAgain,
  isEdit,
  client,
  booking,
  appliedPromos = [],
  // The guide removed the booking's own coupon in this edit. Needed because an untouched edit and a
  // removal both leave appliedPromos empty, and the request alone can't otherwise tell them apart.
  isOriginalDiscountRemoved = false,
  // DS-4473. Recovering an expired booking request: the same edit save, but the backend also
  // revalidates the new slot and accepts the reservation in the one call. Two calls would leave a
  // window where the reservation sits on a new date unaccepted and another booking can take it.
  isRecovery = false,
  // DS-4473. Adjusting a completed booking. Only affects whether the card is sent with the save —
  // the field locking lives in the form.
  isPostTrip = false
}) => {
  let bookingRes = null
  try {
    // Step 1: Validate currently mounted fields, then collect all preserved values
    await form.validateFields()
    const values = form.getFieldsValue(true)
    const [start_time, end_time] = (!isMultiDay && bookingSelections?.timeSlot?.split(" - ")) || []
    // A new booking discounted to nothing has no charge to place. The modal hides the payment-method
    // chooser for it, so whatever the form still holds says nothing about intent — save it as cash,
    // and keep the card (and the Stripe intent below) out of it entirely. Edits are left alone: a
    // zero total there is a refund, which is its own flow.
    const hasNothingToCollect = !isEdit && Number(bookingSelections?.amount ?? 0) <= 0
    const paymentType = hasNothingToCollect ? PAYMENT_OPTIONS.full_cash : getPaymentType(values, booking)
    // Detect an experience swap during edit: the selected experience's uuid differs from the
    // booking's original trip. Book-again creates a fresh booking, so it's not a "change".
    const isExperienceChanged =
      !!isEdit && !isBookAgain && !!booking?.trip?.uuid && values?.experience?.value !== booking.trip.uuid
    // A refund only applies when the edited total drops below what the client has already paid.
    // is_refund must reflect that — NOT the collect-payment flag (which defaults to "yes" and made
    // is_refund true on every edit, including price increases / experience swaps that cost more).
    // collectPayment="no" still suppresses it — it defaults to "yes" (see populateFormWithBooking),
    // and post-trip adjustments deliberately set it to "no" so a completed trip doesn't auto-refund.
    const { refundDueNow = 0 } = isEdit ? calculatePaymentBreakdown({ booking, bookingSelections }) : {}
    const is_refund = refundDueNow > 0 && values?.collectPayment !== "no"
    // Step 2: Build and serialize payload
    const clientAttribute = (bookingUuid && isBookAgain) || !bookingUuid
    const payload = buildTripPayload({
      values,
      bookingSelections,
      timeRange: { start_time, end_time },
      paymentType,
      paymentProvider: values?.paymentType ? paymentProvider[values.paymentType] : null,
      clientInfo: {
        includeClientAttributes: clientAttribute,
        clientId,
        client,
        is_refund
      },
      selectedPaymentMethodId: hasNothingToCollect ? null : selectedPaymentMethodId,
      isEdit,
      isExperienceChanged,
      depositType: booking?.deposit_type,
      appliedPromos,
      existingBookingAddOns: isEdit ? booking?.booking_add_ons : [],
      isPostTrip
    })

    const { promo_codes, ...restPayload } = payload
    // `accept` turns the save into validate → save → accept as one commit. The slot half is
    // atomic; a failed charge still leaves the reservation accepted with a balance outstanding.
    const submittedPayload = isRecovery ? { ...restPayload, accept: true } : restPayload
    const formData = serialize({ trip_booking: submittedPayload }, { indices: true })
    promo_codes?.forEach((code) => formData.append("trip_booking[promo_codes][]", code))
    // Removing the coupon has to be said out loud. promo_codes is appended by hand, so an empty
    // list simply omits the key — byte-identical to an edit that never touched the coupon, which the
    // backend reads as "no change" and keeps it. The key is sent without the [] suffix here so the
    // parameter arrives as trip_booking[promo_codes] rather than as a one-element list, and only on
    // an actual removal, so ordinary edits keep the booking's coupon exactly as before.
    if (isEdit && isOriginalDiscountRemoved && promo_codes?.length === 0) {
      formData.append("trip_booking[promo_codes]", "[]")
    }
    // Step 3: Save booking
    bookingRes = await dispatch(saveGuideBooking(bookingUuid, formData, isBookAgain))

    // Step 4: Process payment if needed
    const newBookingUuid = bookingRes?.uuid || bookingRes?.data?.uuid
    const bookingId = bookingRes.id || bookingRes?.data?.id
    const bookingGuestToken = bookingRes?.guest_token || bookingRes?.data?.guest_token
    
    // PowerTranz is charged by the caller on the hosted page, never through Stripe.
    if (selectedPaymentMethodId && !isEdit && !hasNothingToCollect && values?.paymentType !== "powertranzCard") {
      const paymentRes = await dispatch(createPaymentIntent(bookingId, bookingGuestToken))
      if (!paymentRes?.client_secret) {
        throw new Error("Payment intent creation failed. No client secret returned.")
      }

      // Step 5: Confirm payment
      const result = await stripe.confirmCardPayment(paymentRes.client_secret, {
        payment_method: selectedPaymentMethodId
      })

      if (result?.error) {
        const error = new Error(result.error.message || "Card payment failed.")
        error.retryBookingUuid = newBookingUuid
        throw error
      }

      const paymentIntentId = result?.paymentIntent?.id
      if (!paymentIntentId) {
        throw new Error("No paymentIntent ID received from Stripe.")
      }

      // Step 6: Capture payment
      const captureRes = await dispatch(capturePayment(bookingId, paymentRes?.income?.id))
      if (captureRes?.error) {
        throw new Error("Payment capture failed.")
      }
    }

    // A save can succeed with only part of the refund issued: the booking IS updated, but the
    // client is still owed the rest. `refund_error` says so, and reporting that as a plain success
    // would hide a debt nobody is then tracking — so it takes the place of the success toast, in
    // every flow including recovery.
    const refundError = bookingRes?.refund_error ?? bookingRes?.data?.refund_error

    // Recovery reports its own outcome — the reservation can come back accepted but unpaid, which
    // "Booking updated successfully!" would misrepresent. The caller toasts from the response.
    if (refundError) {
      message.warning({ content: refundError, duration: 10 })
    }
    // No success toast: BookingWindow confirms the save in a modal ("Reservation is Confirmed" /
    // "Reservation is Updated"), and recovery toasts its own outcome.
    return bookingRes
  } catch (err) {
    console.error("Booking Submission Error:", err)
    const newBookingUuid = bookingRes?.uuid || bookingRes?.data?.uuid

    // Throw error with retry UUID if available
    const error = new Error(err.message)
    if (newBookingUuid) {
      error.retryBookingUuid = newBookingUuid
    }
    throw error
  }
}

// An order's payment_option by payment method. An order takes the whole price now: its deposit
// waits on the backend's answer about each experience's own deposit percentage.
const ORDER_PAYMENT_OPTIONS = {
  card: PAYMENT_OPTIONS.full_now,
  link: PAYMENT_OPTIONS.full_link,
  paypalLink: PAYMENT_OPTIONS.full_link,
  cash: PAYMENT_OPTIONS.full_cash
}

// One experience of a New Booking order (`items` of useAddedExperiences) as an order item; the
// item's key is its client_item_id, which the API's errors name. A ticketed experience books its
// start time and tickets, any other its duration, start time and guests.
// The tickets chosen for a ticketed experience, as the API's ticket_lines.
const buildTicketLines = (selections) =>
  Object.entries(selections?.ticketCounts || {})
    .filter(([, quantity]) => quantity > 0)
    .map(([ticketTypeId, quantity]) => ({ ticket_type_id: Number(ticketTypeId), quantity }))

// `adjustment` ({ reason }) when the guide adjusted the order's subtotal: each experience is booked
// at its share of it (selections.baseAmount, as New Booking priced it), recorded as its own adjusted
// subtotal like a single booking's.
const buildOrderItem = ({ key, values, selections }, adjustment) => {
  const item = {
    client_item_id: String(key),
    experience_id: values?.experience?.value,
    note: values?.notes,
    booking_add_ons_attributes: buildAddOnSelectionsAttributes(selections?.addOns, []),
    ...(adjustment
      ? { adjusted_subtotal: Number(selections?.baseAmount) || 0, ...(adjustment.reason ? { adjustment_reason: adjustment.reason } : {}) }
      : {})
  }
  if (selections?.scheduledSlot) {
    return { ...item, experience_time_slot_id: selections.scheduledSlot.id, ticket_lines: buildTicketLines(selections) }
  }
  const [start_time, end_time] = selections?.timeSlot?.split(" - ") || []
  return {
    ...item,
    trip_price_id: selections?.tripLength?.key,
    booked_date: selections?.date,
    ...(selections?.endDate ? { duration_days: countDays(selections.date, selections.endDate) } : {}),
    start_time,
    end_time,
    ...splitGuestCounts(selections)
  }
}

// Accessibility is per experience on the form but per order in the API (asked to move it into
// items[]): the order needs it when any experience does, with each one's notes under its title.
const buildOrderAccessibility = (items) => {
  const needing = items.filter((item) => item.values?.accessibility_required)
  return {
    accessibility_enabled: needing.length > 0,
    accessibility_notes: needing
      .filter((item) => item.values?.accessibility_notes)
      .map((item) => (needing.length > 1 ? `${item.experience?.title}: ${item.values.accessibility_notes}` : item.values.accessibility_notes))
      .join("\n")
  }
}

const buildOrderPayload = ({ values, items, clientId, client, isNothingToCollect }) => {
  const paymentType = isNothingToCollect ? "cash" : values?.paymentType
  const clientAttributes = clientId ? null : buildClientAttributes(values, client)
  const adjustment = Number(values?.adjustedPrice) > 0 ? { reason: values?.adjustmentReason } : null
  return {
    ...(clientId ? { client_id: clientId } : { client_attributes: clientAttributes }),
    payment_option: ORDER_PAYMENT_OPTIONS[paymentType],
    ...(paymentType === "link" || paymentType === "paypalLink" ? { payment_provider: paymentProvider[paymentType] } : {}),
    ...buildOrderAccessibility(items),
    items: items.map((item) => buildOrderItem(item, adjustment))
  }
}

// What a refused order create says: the API's text, and the experience it names (`item`, or null).
const readOrderError = (error, items) => {
  const messages = error?.data?.message
  if (typeof messages === "string") return { text: messages, item: null }
  const { message: text, cartId } = getOrderError(error)
  const item = (text && cartId != null && items.find((candidate) => String(candidate.key) === String(cartId))) || null
  return { text, item }
}

/**
 * Books a New Booking order of several experiences (guide-orders-backend.md, G1/G2): creates the
 * order, whose experiences all become bookings at once, then takes a card payment for the whole
 * order (a link or cash needs nothing more; the backend emails the links). Resolves with the order.
 *
 * A refused create books nothing; its error carries `refusedItem` when the API names the experience.
 * A failed card payment leaves the order booked with the payment owed, and the error carries
 * `retryOrderUuid`: submitting again with it pays that order instead of creating another.
 */
export const submitGuideOrder = async ({
  form,
  dispatch,
  stripe,
  items,
  orderTotal,
  selectedPaymentMethodId,
  clientId,
  client,
  retryOrderUuid = null
}) => {
  const values = form.getFieldsValue(true)
  // Discounted to nothing: no charge to place, so it books as cash (as a single booking does).
  const isNothingToCollect = Number(orderTotal) <= 0
  let order = null
  if (!retryOrderUuid) {
    try {
      order = await dispatch(createGuideOrder(buildOrderPayload({ values, items, clientId, client, isNothingToCollect })))
    } catch (error) {
      // "Morning Bass Trip: This time slot is fully booked". The error carries the experience
      // (`refusedItem`, { key, message }) for the form to mark it.
      const { text, item } = readOrderError(error, items)
      const title = item ? i18n.t("booking.added_experiences.refused_toast", { title: item.experience?.title, message: text }) : text
      toaster.error({ title: title || i18n.t("errors.not_valid") })
      const createError = new Error(text)
      createError.refusedItem = item ? { key: item.key, message: text } : null
      throw createError
    }
  }
  const orderUuid = retryOrderUuid || order?.uuid
  // A retry has no create response: the order is read back for its number.
  const getOrder = async () => order ?? (await dispatch(fetchGuideOrder(orderUuid)).catch(() => ({ uuid: orderUuid })))
  if (isNothingToCollect || values?.paymentType !== "card" || !selectedPaymentMethodId) return getOrder()

  try {
    const payment = await dispatch(createGuideOrderPayment(orderUuid))
    if (payment?.card_payments_available === false) throw new Error(payment.message || i18n.t("form_validation.payment_provider_missing"))
    // Nothing left to pay now.
    if (!payment?.client_secret) return getOrder()
    const result = await stripe.confirmCardPayment(payment.client_secret, { payment_method: selectedPaymentMethodId })
    if (result?.error) throw new Error(result.error.message || i18n.t("booking.added_experiences.card_payment_failed"))
    return await dispatch(captureGuideOrderPayment(orderUuid, payment.id))
  } catch (error) {
    if (error instanceof Error) toaster.error({ title: error.message })
    const paymentError = new Error(error?.message)
    paymentError.retryOrderUuid = orderUuid
    throw paymentError
  }
}

export const validatePaymentAmount = (value, { maxAmount, isRefund, t } = {}) => {
  // Handle empty/undefined/null case first
  if (value === "" || value === null || value === undefined) {
    return Promise.reject(t("booking.validation.record_payment_required"))
  }

  const numericValue = Number.parseFloat(value)

  // Check for invalid number
  if (Number.isNaN(numericValue)) {
    return Promise.reject(t("booking.validation.invalid_number"))
  }

  // Check for non-positive numbers
  if (numericValue <= 0) {
    return Promise.reject(t("booking.validation.amount_positive"))
  }

  // Check against maximum amount if provided
  if (maxAmount !== undefined && numericValue > maxAmount) {
    return isRefund ? Promise.reject(t("booking.validation.exceeds_refund")) : Promise.reject(t("booking.validation.exceeds_balance"))
  }

  // Validation passed
  return Promise.resolve()
}

// Detects whether the user has made any user-visible change to the booking inputs
// (guest counts, trip duration, or add-ons) compared to the booking's saved state.
// Used to skip recalculation on the initial edit view — the booking's stored total is the
// source of truth until the user actually changes something, so recomputing can only introduce
// drift (e.g. when experience settings have changed since the booking was created, or when
// data hasn't fully loaded yet).
// A saved booking's last day: the last of its booked_dates (the server's full date list), else
// booked_date plus duration_value days.
const getBookingEndDate = (booking) =>
  booking?.booked_dates?.[booking.booked_dates.length - 1] ||
  dayjs(booking?.booked_date)
    .add((Number(booking?.duration_value) || 1) - 1, "day")
    .format("YYYY-MM-DD")

// A saved ticketed booking's tickets as the form keeps them: { [ticketTypeId]: quantity }.
const getBookingTicketCounts = (booking) =>
  Object.fromEntries((booking?.ticket_lines || []).map((line) => [line.ticket_type_id, Number(line.quantity) || 0]))

// Whether a ticketed selection still books the saved departure, tickets and add-ons.
const isTicketedSelectionUnchanged = (bookingSelections, booking) => {
  const savedCounts = getBookingTicketCounts(booking)
  const counts = bookingSelections.ticketCounts || {}
  const typeIds = new Set([...Object.keys(savedCounts), ...Object.keys(counts)])
  const isSameTickets = [...typeIds].every((id) => (Number(counts[id]) || 0) === (savedCounts[id] || 0))
  const originalAddOnsTotal = (booking.booking_add_ons || []).reduce((sum, item) => sum + Number(item.total_price || 0), 0)
  return (
    String(bookingSelections.scheduledSlot?.id) === String(booking.experience_time_slot_id) &&
    isSameTickets &&
    Math.abs(Number(bookingSelections.addOnsTotal || 0) - originalAddOnsTotal) <= 0.01
  )
}

export const hasNoUserBookingChanges = (bookingSelections, booking, isExperienceChanged = false, isPromoChanged = false) => {
  if (!booking || !bookingSelections) return false

  // Swapping the experience is a change in itself, whatever the date and guest counts say. It is
  // not represented in bookingSelections, so without this the whole comparison below can report
  // "nothing changed" for a booking now pointing at a different trip — every recalculation path
  // then bails out and keeps the saved total, including a discount whose coupon does not apply to
  // the new experience. The price looks untouched and the old coupon stays on screen.
  if (isExperienceChanged) return false

  // Adding or removing a coupon is a user change too, and it is no more visible in
  // bookingSelections than the experience is. Without this, changing only the promo leaves
  // every payment-type recalculation gated behind a "nothing changed" that is false: switching
  // to card after removing a coupon then keeps a total with no platform or card processing fee.
  if (isPromoChanged) return false

  // A ticketed booking is its departure and tickets, not guest counts and a duration.
  if (booking.experience_time_slot_id) return isTicketedSelectionUnchanged(bookingSelections, booking)

  const originalCounts = {
    adults: sumGuestTypeCount("adult", booking),
    children: sumGuestTypeCount("child", booking),
    infants: sumGuestTypeCount("infant", booking),
    passengers: sumGuestTypeCount("passenger", booking)
  }
  const main = bookingSelections.counts?.main || {}
  const extra = bookingSelections.counts?.extra || {}
  const guestCountChanged = ["adults", "children", "infants", "passengers"].some(
    (key) => (Number(main[key]) || 0) + (Number(extra[key]) || 0) !== originalCounts[key]
  )

  const originalAddOnsTotal = (booking.booking_add_ons || []).reduce((sum, item) => sum + Number(item.total_price || 0), 0)
  const addOnsChanged = Math.abs(Number(bookingSelections.addOnsTotal || 0) - originalAddOnsTotal) > 0.01

  // Negotiated-subtotal bookings pin the base to the adjusted price, so date/duration edits never
  // change the total and are treated as "no change" (the "price not changed" view is shown for them).
  // Guest and add-on edits still count as changes: guest edits so the breakdown reflects the new
  // counts (base stays the adjusted price), add-on edits because they change the fees/total.
  if (booking?.payment_summary?.subtotal_adjusted) {
    return !guestCountChanged && !addOnsChanged
  }

  // A date change can move the trip onto/off a day_adjustment (e.g. weekend surcharge) or a
  // seasonal_adjustment (e.g. a holiday range), so it must trigger a recalculation even if
  // guest counts/duration/add-ons are unchanged.
  if (bookingSelections.date && bookingSelections.date !== booking.booked_date) return false

  // A dynamic multi-day's range end is tracked separately from its start (`endDate`, set only by
  // the range picker — see handleRangeChange). Extending or shortening the stay while leaving the
  // start date alone leaves `bookingSelections.date` unchanged, so without this the check above
  // misses it entirely and the whole date-range edit reads as "nothing changed" — skipping
  // recalculation and leaving the stale saved total on screen (the price only updated when a
  // guest-count change happened to accompany it, since that still trips guestCountChanged below).
  if (bookingSelections.endDate) {
    if (bookingSelections.endDate !== getBookingEndDate(booking)) return false
  }

  if (guestCountChanged) return false

  if (bookingSelections.tripLength?.value !== booking.selected_trip_duration) return false

  if (addOnsChanged) return false

  return true
}

// Maps a booking's saved payment_option to the form's `paymentType` vocabulary.
// Used as a fallback during edit-mode recalculation: the form's `paymentType` can be
// transiently undefined (the PaymentMethod component, which authoritatively sets it, only
// mounts on Step 2 — and not at all when a refund is shown). A calc with an undefined
// paymentType silently drops the platform fee and card processing fee, lowering the total and
// producing a phantom "refund due". Falling back to the booking's payment type keeps the fees.
const EDIT_PAYMENT_OPTION_TO_TYPE = {
  [PAYMENT_OPTIONS.full_now]: "card",
  [PAYMENT_OPTIONS.deposit_later]: "card",
  [PAYMENT_OPTIONS.deposit_cash]: "card",
  [PAYMENT_OPTIONS.deposit_link]: "card",
  [PAYMENT_OPTIONS.deposit_link_in_cash]: "card",
  [PAYMENT_OPTIONS.pay_later]: "card",
  [PAYMENT_OPTIONS.full_cash]: "cash",
  [PAYMENT_OPTIONS.full_link]: "link",
  [PAYMENT_OPTIONS.deposit_amount_by_link]: "link"
}

export const deriveBookingPaymentType = (booking) => EDIT_PAYMENT_OPTION_TO_TYPE[booking?.payment_option] || "link"

// Cumulative post-creation price adjustment made via the booking-details "Adjust Booking Price"
// flow. Each entry in payment_summary.adjust_price_history holds a `price_difference` (already net
// of its own fees — it sits after Tax & Fees, before the total) and is baked into total_price. The
// sum is carried into edit-mode recalcs as a flat total-level delta so the manual price change
// persists through edits, matching the details-page breakdown.
export const getPriceAdjustmentTotal = (booking) =>
  (booking?.payment_summary?.adjust_price_history ?? []).reduce((sum, item) => sum + Number(item?.price_difference || 0), 0)

// Returns the payment type to use for a recalculation. The form value (an explicit user choice)
// always wins; only when it is missing do we fall back to the booking's saved payment type, and
// only in edit mode (a new booking has no booking to fall back to).
export const resolveEditPaymentType = (paymentType, booking, isEdit) =>
  paymentType || (isEdit ? deriveBookingPaymentType(booking) : paymentType)

export const populateFormWithBooking = async ({ booking, bookingUuid, dispatch, form, isBookAgain }) => {
  if (bookingUuid && booking) {
    // do nothing
  } else {
    form.setFieldsValue(initialValues)
    return
  }

  const client = booking.client
  const trip = booking.trip

  // Subtotal adjustment (set at creation): carry the negotiated base into edit mode so the same
  // adjusted price drives the display and calculation. It's read-only here — the guide can't
  // re-adjust while editing — but must persist until (a future flow adds) a new adjusted price.
  const paymentSummary = booking?.payment_summary ?? {}
  const isSubtotalAdjusted = !!paymentSummary.subtotal_adjusted && paymentSummary.adjusted_subtotal != null
  const adjustedBaseAmount = isSubtotalAdjusted ? Number(paymentSummary.adjusted_subtotal) : null
  const computedBaseAmount = computeGuestSubtotalFromPaymentSummary(paymentSummary)
  const originalBaseAmount = Number(paymentSummary.original_subtotal) || computedBaseAmount

  const paymentOptionMap = {
    full_amount_today: "card",
    deposit_and_balance_later: "card",
    deposit_and_balance_in_cash: "card",
    full_amount_in_cash_on_day: "cash",
    full_amount_by_link: "link",
    deposit_by_link_and_balance_later: "link",
    deposit_by_link_and_balance_in_cash: "link",
    pay_later: "card"
  }
  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash } = PAYMENT_OPTIONS

  const paymentType = paymentOptionMap[booking?.payment_option] || "link"
  const depositPercent = [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(booking?.payment_option)
  dispatch(setSelectedPaymentMethodId(booking?.last_selected_payment_method))
  // user_id, not the client record id: every other writer of this field stores user_id (the
  // existing-client dropdown, and the booking-detail screen), and it is what the backend reads as
  // client_id. Falls back to `id` only if the payload has no user_id, preserving the old value.
  dispatch(setClientId(client?.user_id ?? client?.id))
  await form.setFieldsValue({
    firstName: client.first_name,
    lastName: client.last_name,
    email: client.email,
    phone_number: { fullNumber: client?.phone_number, countryCode: client?.country_code },
    experience: {
      label: trip?.title,
      value: trip?.uuid,
      key: trip?.id
    },
    date: dayjs(booking.booked_date),
    duration: booking?.selected_trip_duration,
    time: `${booking?.start_time} - ${booking?.end_time}`,
    accessibility_required: booking?.accessibility_enabled,
    send_guest_email: booking?.send_guest_email ?? true,
    client_notify: booking?.send_guest_email ?? true,
    accessibility_notes: booking?.accessibility_notes,
    depositOnly: depositPercent,
    depositPercent: depositPercent ? booking?.deposit_percentage : null,
    paymentOption: booking?.payment_option,
    paymentType,
    notes: booking?.note,
    collectPayment: booking?.collect_payment || "yes",
    adjustedPrice: adjustedBaseAmount,
    adjustmentReason: isSubtotalAdjusted ? paymentSummary.subtotal_adjustment_reason : null
  })

  if (isBookAgain) {
    dispatch(resetExperienceBookingData())
    // Book-again is a fresh booking — don't carry the source booking's negotiated subtotal; the
    // guide re-adjusts from scratch on the create screen.
    form.setFieldsValue({ date: null, duration: "", time: "", depositOnly: false, depositPercent: null, adjustedPrice: null, adjustmentReason: null })
    return null
  }

  const existingAddOns = (booking.booking_add_ons || []).reduce((acc, item) => {
    const id = item.add_on_id
    if (id) acc[id] = item.quantity
    return acc
  }, {})
  const existingAddOnsTotal = (booking.booking_add_ons || []).reduce((sum, item) => sum + Number(item.total_price || 0), 0)

  // Dynamic multi-day has no trip_price to re-derive the span from (TripPrice.duration_days is
  // always nil for dynamic trips) — the booking's own frozen span is the only source. booked_dates
  // is already the full, server-computed date list (booked_date .. booked_date + duration_value - 1),
  // so its last entry is the end date; duration_value is the fallback if booked_dates is ever absent.
  const endDate = isDynamicMultiDay(trip) ? getBookingEndDate(booking) : undefined

  const counts = {
    main: {
      adults: sumGuestTypeCount("adult", booking),
      children: sumGuestTypeCount("child", booking),
      infants: sumGuestTypeCount("infant", booking),
      passengers: sumGuestTypeCount("passenger", booking)
    },
    extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
  }

  // A ticketed booking (QA Ticketing #57) in TicketedFields' shape: its departure and tickets.
  const ticketCounts = getBookingTicketCounts(booking)
  const ticketedFields = booking.experience_time_slot_id
    ? {
        scheduledSlot: { id: booking.experience_time_slot_id, startsAt: booking.experience_time_slot?.starts_at || null },
        ticketCounts,
        totalGuests: Object.values(ticketCounts).reduce((sum, quantity) => sum + quantity, 0)
      }
    : {}

  return {
    date: booking.booked_date,
    ...(endDate ? { endDate } : {}),
    ...ticketedFields,
    timeSlot: `${booking?.start_time} - ${booking?.end_time}`,
    tripLength: {
      key: booking?.trip_schedule?.id,
      value: booking?.selected_trip_duration,
      // Carries the unit prices the booking was charged, so an edit that leaves the date and guests
      // alone (add-ons only) keeps the guests at those prices, as the save does.
      price: withSoldUnitPrices(booking?.trip_schedule, paymentSummary, booking.booked_date, counts)
    },
    counts,
    addOns: existingAddOns,
    addOnsTotal: existingAddOnsTotal,
    // The charged total, not the raw total_price: `amount` is compared against (and later
    // replaced by) calculateBookingTotal's total, which includes the card fee.
    amount: getSavedBookingTotals(booking?.payment_summary).totalCharged,
    // When the subtotal was negotiated at creation, baseAmount is the adjusted (effective) base and
    // originalBaseAmount is the pre-adjustment computed subtotal — mirroring the create-screen fields
    // so PriceBreakdown can render the struck-through original alongside the adjusted price.
    baseAmount: isSubtotalAdjusted ? adjustedBaseAmount : computedBaseAmount,
    originalBaseAmount,
    breakdown: buildBreakdownFromPaymentSummary(booking?.payment_summary)
  }
}
